import React from 'react';
import I18n from '@/modules/i18n';
import { Button } from '@/components/ui/Buttons';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import { IAISuggestion } from '@/components/staffer/Grading/types';
import cn from '@/utils/cn';
import AISuggestionChip from './AISuggestionChip';
import { displayedSuggestedScore } from './aiSuggestion';

// AIDEV-NOTE: the AI grading component sheet keeps hover and focus the same color as default; only press
// changes (a deeper shadow). The `!` classes outrank the .btn--* hover/active/disabled rules.
const COLOR_CLASSES = {
  green: '!tw-bg-green !tw-border-green !tw-text-white disabled:!tw-bg-dark-grey disabled:!tw-border-dark-grey',
  red: '!tw-bg-white !tw-border-red !tw-text-red disabled:!tw-border-dark-grey disabled:!tw-text-dark-grey',
};

interface ReviewButtonProps {
  children: React.ReactNode;
  color: 'green' | 'red';
  disabled: boolean;
  onClick: () => void;
}

const ReviewButton = ({ children, color, disabled, onClick }: ReviewButtonProps) => (
  <Button
    className={cn(
      '!tw-inline-flex tw-items-center !tw-h-8 !tw-px-2 !tw-py-0',
      '!tw-shadow enabled:active:!tw-shadow-md !tw-transition-colors !tw-duration-100',
      'disabled:!tw-transition-none disabled:!tw-opacity-100',
      COLOR_CLASSES[color],
    )}
    color={color}
    disabled={disabled}
    outlineStyle={color === 'red'}
    onClick={onClick}
  >
    {children}
  </Button>
);

interface AISuggestionReviewProps {
  aiSuggestion: IAISuggestion;
  isBusy: boolean;
  onAccept: () => void;
  onReject: () => void;
}

const AISuggestionReview = ({ aiSuggestion, isBusy, onAccept, onReject }: AISuggestionReviewProps) => {
  const { assignment } = useGradingStore();
  const { state, suggested_score: suggestedScore } = aiSuggestion;

  return (
    <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2.5 tw-mr-2.5">
      <AISuggestionChip
        score={suggestedScore && displayedSuggestedScore(assignment, suggestedScore)}
        state={state}
      />
      {state === 'ai_suggested_pending' && (
        <>
          <ReviewButton color="green" disabled={isBusy} onClick={onAccept}>{I18n.t('accept')}</ReviewButton>
          <ReviewButton color="red" disabled={isBusy} onClick={onReject}>{I18n.t('reject')}</ReviewButton>
        </>
      )}
    </div>
  );
};

export default AISuggestionReview;
